/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Tabs,
  Tab,
  TextArea,
  TabList,
  TabPanels,
  TabPanel
} from '@carbon/react';
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../store';
import { SearchResultDialogs } from '../../discovery/components/search-detail-config/search-result-dialogs/SearchResultDialogs';
import { ConversationalSearchSection } from '../../ia/components/conversational-search/ConversationalSearchSection';
import {
  setCustomServiceFilterValue,
  setCustomServiceMetadataValue,
  setIsCustomServiceSetupValid
} from '../store/reducer';
import { selectFilter, selectMetadata } from '../store/selectors';
import { CustomServiceInstanceConfig } from './custom-service-instance-config/CustomServiceInstanceConfig';
import { messages } from './messages';
import '../../elastic-search/components/ElasticSearchTabs.scss';

interface CustomServiceTabsProps {
  noResultsMessage: string;
  setNoResultsMessage: (value) => void;
  connectivityMessage: string;
  setConnectivityMessage: (value) => void;
  readOnly: boolean;
  draft: {
    setCitationsTitleValue: (string) => { payload: any; type: string };
    setMaxCitationsValue: (number) => void;
    setIsConversationalSearchEnabled: (string) => {
      payload: any;
      type: string;
    };
    isConversationalSearchEnabled: boolean;
    setConversationalSearchRetrievalConfidenceThreshold: (string) => void;
    conversationalSearchRetrievalConfidenceThreshold: string;
    setConversationalSearchConfidenceThreshold: (string) => void;
    conversationalSearchConfidenceThreshold: string;
    setConversationalSearchVerbosityOption: (string) => void;
    conversationalSearchVerbosityOption: string;
    setConversationalSearchResponseConfidenceThreshold: (string) => void;
    conversationalSearchResponseConfidenceThreshold: string;
    citationsTitleValue: string;
    maxCitationsValue: number;
    setConversationalSearchMultiTurn: (boolean) => void;
    isConversationalSearchMultiTurnEnabled: boolean;
  };
  onClose: () => void;
}

const metadataPlaceholder = `JSON example:

{
  "example_field": "example_value",
  "other_example_field": 7
}`;

export const CustomServiceTabs = ({
  noResultsMessage,
  setNoResultsMessage,
  connectivityMessage,
  setConnectivityMessage,
  readOnly,
  draft,
  onClose
}: CustomServiceTabsProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const customServiceFilter = useAppSelector(selectFilter);
  const [filter, setFilter] = useState(customServiceFilter ?? '');

  const customServiceMetadata = useAppSelector(selectMetadata);
  const [metadata, setMetadata] = useState(
    customServiceMetadata ? JSON.stringify(customServiceMetadata) : ''
  );

  const [invalidMetadata, setInvalidMetadata] = useState(false);
  const [invalidMetadataText, setInvalidMetadataText] = useState('');

  const filterChange = e => {
    const filter = e?.target?.value ?? '';
    setFilter(filter);
    dispatch(setCustomServiceFilterValue(filter));
  };

  const metadataChange = e => {
    const metadata = e?.target?.value ?? '';
    setMetadata(metadata);
    if (metadata) {
      let metadataObject;
      // Store expects an object for the metadata so we need to convert back and forth from a string to an object.
      try {
        // JSON.parse thinks the json is invalid if there are any new line characters in the string. So first remove all
        // the white space OUTSIDE the key/values and then try to transform the string to an object.
        metadataObject = JSON.parse(metadata.replace(/(\r\n|\n|\r)/gm, ''));
      } catch (e) {
        console.error(e);
        // If the metadata is not valid json then show a validation error.
        setInvalidMetadata(true);
        setInvalidMetadataText(t(messages.invalidJsonObjectValue));
        return;
      }

      // If metadataObject is not an object (or is an array) then show a validation error.
      if (typeof metadataObject !== 'object' || Array.isArray(metadataObject)) {
        setInvalidMetadata(true);
        setInvalidMetadataText(t(messages.invalidJsonObjectValue));
      } else {
        // If the metadata is a valid json object then clear any validation errors and save the object.
        setInvalidMetadata(false);
        setInvalidMetadataText('');
        dispatch(setCustomServiceMetadataValue(metadataObject));
      }
    } else {
      // If the metadata is blank then clear any validation errors and save the blank input.
      setInvalidMetadata(false);
      setInvalidMetadataText('');
      dispatch(setCustomServiceMetadataValue(metadata));
    }
  };

  // Update the parent panel on the validity of the inputs for the custom service config.
  useEffect(() => {
    if (invalidMetadata) {
      dispatch(setIsCustomServiceSetupValid(false));
    } else {
      dispatch(setIsCustomServiceSetupValid(true));
    }
  }, [dispatch, invalidMetadata]);

  return (
    <div className="CustomServiceSearch">
      <div className="CustomServiceSearch__container">
        <Tabs>
          <TabList contained aria-label="CustomServiceSearch">
            <Tab>{t(messages.settings)}</Tab>
            <Tab>{t(messages.instance)}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel id="CustomServiceSearchTabs-settings">
              <div className="CustomServiceSearch__settings">
                <div className="CustomServiceSearch__settings-container">
                  <ConversationalSearchSection
                    draft={draft}
                    readOnly={readOnly}
                  />
                  <h4 className={'SearchResultDialogs__title'}>
                    {t(messages.defaultFilter)}
                  </h4>
                  <TextArea
                    className={'SearchResultDialogs__custom-filter'}
                    labelText={t(messages.defaultFilter)}
                    hideLabel
                    rows={6}
                    cols={150}
                    maxLength={1024}
                    id="CustomServiceSearchTabs-filter"
                    placeholder={t(messages.defaultFilterPlaceholder)}
                    onChange={filterChange}
                    value={filter}
                    disabled={readOnly}
                  />
                  <h4 className={'SearchResultDialogs__title'}>
                    {t(messages.metadata)}
                  </h4>
                  <TextArea
                    className={'SearchResultDialogs__custom-filter'}
                    labelText={t(messages.metadata)}
                    hideLabel
                    rows={6}
                    cols={150}
                    id="CustomServiceSearchTabs-metadata"
                    placeholder={metadataPlaceholder}
                    onChange={metadataChange}
                    invalid={invalidMetadata}
                    invalidText={invalidMetadataText}
                    value={metadata}
                    disabled={readOnly}
                  />
                  <SearchResultDialogs
                    hideSuccessMessageTab={true}
                    noResultsMessage={noResultsMessage}
                    setNoResultsMessage={setNoResultsMessage}
                    connectivityMessage={connectivityMessage}
                    setConnectivityMessage={setConnectivityMessage}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel id="CustomServiceSearchTabs-instanced">
              <CustomServiceInstanceConfig
                noHeader={true}
                readOnly={readOnly}
                onClose={onClose}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  );
};
